import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  ConnectionStatus,
  GrantState,
  OrgGrant,
  Provider,
} from '../../../api-clients/oem-connection';
import { OemIntegrationService } from '../../../services/oem-integration.service';

interface HealthResult {
  ok: boolean;
  lastChecked: string;
  error?: string;
}

/**
 * Vendor connectivity health-check panel (SRA003-390 Phase 1).
 *
 * Surfaces a single "Test connection" button on the OEM manage-view page so
 * the contractor can verify Harvest Mate can still reach the vendor. Calls the
 * shared OemIntegrationService.testConnection, now wired to the real endpoint
 * (SRA003-391 / BE #264: POST /oem-connection/{provider}/test, success "ok").
 */
@Component({
  selector: 'app-connection-health-panel',
  templateUrl: './connection-health-panel.component.html',
  styleUrls: ['./connection-health-panel.component.css'],
  standalone: false,
})
export class ConnectionHealthPanelComponent {
  @Input() provider!: Provider;
  @Input() providerLabel: string = '';
  @Input() status: ConnectionStatus | null = null;

  isChecking = false;
  lastResult: HealthResult | null = null;

  // FE-190 Stage 2 — Enable live data CTA (Deere DSS subscription).
  isEnabling = false;

  // SRA003-430 — reconnect CTA for a terminally-expired connection.
  isReconnecting = false;

  constructor(
    private oemSvc: OemIntegrationService,
    private toastr: ToastrService,
  ) {}

  testConnection(): void {
    this.isChecking = true;
    this.lastResult = null;
    this.oemSvc.testConnection(this.provider).subscribe({
      next: (res) => {
        this.isChecking = false;
        this.lastResult = {
          // BE returns status:"ok" (lowercase) — compare case-insensitively.
          // testConnection is wired to the real endpoint (SRA003-404 / BE #264);
          // a strict '=== OK' would render "Failed" on a real success.
          ok: res.status?.toLowerCase() === 'ok',
          lastChecked: res.lastChecked,
        };
      },
      error: (err: HttpErrorResponse) => {
        this.isChecking = false;
        this.lastResult = {
          ok: false,
          lastChecked: new Date().toISOString(),
          error: err?.message || 'Connection test failed',
        };
      },
    });
  }

  /**
   * "Enable live data" — register Deere DSS subscriptions for every granted
   * organisation on this contractor connection (FE-190 Stage 2 / BE PR #268).
   * Deere-only by design (DSS is a Deere concept); the CTA is hidden for CNH
   * and disabled for non-active connections. A 503 maps to a "license pending"
   * toast per the typed-503 pattern (SRA003-411 mirror).
   */
  enableLiveData(): void {
    if (this.isEnabling || !this.canEnableLiveData) return;
    this.isEnabling = true;
    this.oemSvc
      .listDeereGrants()
      .pipe(
        switchMap((grants: OrgGrant[]) => {
          const granted = (grants ?? []).filter(
            (g) => g.grant_status === GrantState.Granted,
          );
          if (granted.length === 0) {
            return throwError(
              () => new Error('No granted Deere organisations to subscribe.'),
            );
          }
          return forkJoin(
            granted.map((g) => this.oemSvc.enableDssSubscribe(g.oem_org_id)),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.isEnabling = false;
          this.toastr.success(
            'Live data enabled — events will flow on next harvest activity.',
          );
        },
        error: (err: HttpErrorResponse | Error) => {
          this.isEnabling = false;
          this.toastr.error(this.liveDataErrorMessage(err));
        },
      });
  }

  /**
   * SRA003-430: the connection's refresh grant is dead — the BE flipped
   * oem_connections.status to 'expired' and /connections passes it through.
   * Terminal state: render non-green with a reconnect CTA, and suppress the
   * healthy affordances (see showLiveDataCta). Distinct from 'revoked'
   * (user-intent disconnect), which is left rendering as before.
   */
  get isExpired(): boolean {
    return this.status?.status === 'expired';
  }

  /**
   * Route the contractor back into the vendor OAuth re-auth flow. Same entry
   * point as a fresh connect (startConnect) — the BE upserts on
   * UNIQUE(contractor, provider), so re-authing replaces the expired row.
   * Provider-agnostic: both Deere and CNH can reach 'expired'.
   */
  reconnect(): void {
    if (this.isReconnecting) return;
    this.isReconnecting = true;
    this.oemSvc.startConnect(this.provider).subscribe({
      error: () => {
        this.isReconnecting = false;
        this.toastr.error(
          `Could not start the reconnect flow for ${this.providerLabel}.`,
        );
      },
    });
  }

  /**
   * Render the CTA only on Deere panels with a real connection. Suppressed
   * when 'expired' (SRA003-430) so a dead connection never offers a
   * live-data affordance as if it were healthy; 'revoked'/'error' keep their
   * existing disabled-with-tooltip rendering.
   */
  get showLiveDataCta(): boolean {
    return (
      this.provider === Provider.Deere &&
      !!this.status &&
      this.status.status !== 'not-connected' &&
      this.status.status !== 'expired'
    );
  }

  /** CTA is clickable only when the connection is currently active. */
  get canEnableLiveData(): boolean {
    return this.showLiveDataCta && this.status?.status === 'active';
  }

  /** State-specific tooltip for the disabled CTA (revoked / expired / error). */
  get liveDataTooltip(): string {
    if (!this.status) return '';
    switch (this.status.status) {
      case 'expired':
        return 'Connection token has expired — re-link your Deere account to enable live data.';
      case 'revoked':
        return 'Deere has revoked this connection — re-link to enable live data.';
      case 'error':
        return 'Connection in error state — re-link your Deere account to enable live data.';
      default:
        return '';
    }
  }

  private liveDataErrorMessage(err: HttpErrorResponse | Error): string {
    if (err instanceof HttpErrorResponse && err.status === 503) {
      return 'Live data activation pending — Deere API license not yet enabled. Try again later or contact support.';
    }
    if (err instanceof HttpErrorResponse) {
      const detail = (err.error as { detail?: string })?.detail;
      return detail || err.message || 'Failed to enable live data.';
    }
    return err.message || 'Failed to enable live data.';
  }
}
